import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';

/**
 * A six-degree-of-freedom rigid body.
 *
 * Positions and velocities are stored in a named reference frame (usually a
 * planet-centred inertial frame, or the root frame in interplanetary space).
 * The body never knows which frame that is; the owning simulation is
 * responsible for re-expressing state when the frame changes, via
 * `ReferenceFrameManager.transformState`.
 *
 * Nothing outside this class is allowed to write `position`, `velocity` or
 * `orientation` during normal gameplay. Control inputs, flight assistance and
 * macros all act through `applyForce`, `applyForceAtPoint` and `applyTorque`.
 * The only legitimate direct writes are teleport-free frame re-expression and
 * save loading, both of which go through `setState`.
 */
export class RigidBody {
  readonly position = new Vec3();
  readonly velocity = new Vec3();
  readonly orientation = Quat.identity();
  /** Angular velocity in the reference frame, rad/s. */
  readonly angularVelocity = new Vec3();

  /** Total mass, kg. Updated by the owning mass model. */
  mass = 1000;
  /** Principal moments of inertia about the centre of mass, body axes, kg m^2. */
  readonly inertia = new Vec3(1000, 1000, 1000);

  /** Accumulated force for the current step, reference frame, N. */
  readonly force = new Vec3();
  /** Accumulated torque for the current step, reference frame, N m. */
  readonly torque = new Vec3();

  /** Acceleration recorded during the last step, for HUD and camera effects. */
  readonly lastAcceleration = new Vec3();
  /** Non-gravitational acceleration only, i.e. what an accelerometer reads. */
  readonly lastProperAcceleration = new Vec3();

  /** Linear damping applied when resting on a surface, 1/s. */
  linearDamping = 0;
  /** Angular damping from structural friction and reaction-wheel drag, 1/s. */
  angularDamping = 0.02;

  clearAccumulators(): void {
    this.force.set(0, 0, 0);
    this.torque.set(0, 0, 0);
  }

  /** Applies a force through the centre of mass (no torque). */
  applyForce(f: Vec3): void {
    this.force.add(f);
  }

  /**
   * Applies a force at an offset from the centre of mass, expressed in the
   * reference frame. This is the function that makes thruster placement matter:
   * an engine mounted off-axis produces both translation and torque.
   */
  applyForceAtPoint(f: Vec3, offsetFromCom: Vec3): void {
    this.force.add(f);
    this.torque.add(Vec3.crossVectors(offsetFromCom, f));
  }

  /** Applies a force given in body coordinates at a body-space offset. */
  applyBodyForceAtPoint(forceBody: Vec3, offsetBody: Vec3): void {
    const f = this.orientation.rotate(forceBody);
    const r = this.orientation.rotate(offsetBody);
    this.applyForceAtPoint(f, r);
  }

  applyTorque(tau: Vec3): void {
    this.torque.add(tau);
  }

  applyBodyTorque(tauBody: Vec3): void {
    this.torque.add(this.orientation.rotate(tauBody));
  }

  /** Applies an instantaneous impulse (N s). Used for collisions only. */
  applyImpulse(impulse: Vec3): void {
    this.velocity.addScaled(impulse, 1 / this.mass);
  }

  applyImpulseAtPoint(impulse: Vec3, offsetFromCom: Vec3): void {
    this.applyImpulse(impulse);
    const angularImpulse = Vec3.crossVectors(offsetFromCom, impulse);
    this.applyAngularImpulse(angularImpulse);
  }

  applyAngularImpulse(angularImpulse: Vec3): void {
    const local = this.orientation.rotateInverse(angularImpulse);
    local.x /= this.inertia.x;
    local.y /= this.inertia.y;
    local.z /= this.inertia.z;
    this.angularVelocity.add(this.orientation.rotate(local));
  }

  /**
   * Integrates rotation for one step.
   *
   * Uses the body-frame Euler equations including the gyroscopic term, so an
   * asymmetric vessel spun about an intermediate axis tumbles the way it should
   * rather than spinning cleanly forever.
   */
  integrateRotation(dt: number): void {
    const omegaBody = this.orientation.rotateInverse(this.angularVelocity);
    const torqueBody = this.orientation.rotateInverse(this.torque);

    const Iw = new Vec3(
      this.inertia.x * omegaBody.x,
      this.inertia.y * omegaBody.y,
      this.inertia.z * omegaBody.z,
    );
    const gyro = Vec3.crossVectors(omegaBody, Iw);

    const alphaBody = new Vec3(
      (torqueBody.x - gyro.x) / this.inertia.x,
      (torqueBody.y - gyro.y) / this.inertia.y,
      (torqueBody.z - gyro.z) / this.inertia.z,
    );

    this.angularVelocity.addScaled(this.orientation.rotate(alphaBody), dt);

    if (this.angularDamping > 0) {
      const k = Math.exp(-this.angularDamping * dt);
      this.angularVelocity.mulScalar(k);
    }

    this.orientation.integrate(this.angularVelocity, dt);
  }

  /** Kinetic energy, joules. */
  kineticEnergy(): number {
    const linear = 0.5 * this.mass * this.velocity.lengthSq();
    const w = this.orientation.rotateInverse(this.angularVelocity);
    const angular =
      0.5 * (this.inertia.x * w.x * w.x + this.inertia.y * w.y * w.y + this.inertia.z * w.z * w.z);
    return linear + angular;
  }

  /** Angular momentum in the reference frame, kg m^2/s. */
  angularMomentum(out = new Vec3()): Vec3 {
    const w = this.orientation.rotateInverse(this.angularVelocity);
    out.set(this.inertia.x * w.x, this.inertia.y * w.y, this.inertia.z * w.z);
    return this.orientation.rotate(out.clone(), out);
  }

  /**
   * Re-expresses the body in a different reference frame, or restores it from a
   * save. This is the only sanctioned direct write to the kinematic state.
   */
  setState(position: Vec3, velocity: Vec3, orientation: Quat, angularVelocity: Vec3): void {
    this.position.copy(position);
    this.velocity.copy(velocity);
    this.orientation.copy(orientation).normalize();
    this.angularVelocity.copy(angularVelocity);
  }

  /** Sets principal moments of inertia from a solid box approximation. */
  setInertiaFromBox(mass: number, sizeX: number, sizeY: number, sizeZ: number): void {
    const k = mass / 12;
    this.inertia.set(
      k * (sizeY * sizeY + sizeZ * sizeZ),
      k * (sizeX * sizeX + sizeZ * sizeZ),
      k * (sizeX * sizeX + sizeY * sizeY),
    );
  }

  /** Body +Z axis in the reference frame; the vessel's nose direction. */
  forward(out = new Vec3()): Vec3 {
    return this.orientation.axisZ(out);
  }

  /** Body +Y axis in the reference frame; the vessel's dorsal direction. */
  up(out = new Vec3()): Vec3 {
    return this.orientation.axisY(out);
  }

  /** Body +X axis in the reference frame; the vessel's starboard direction. */
  right(out = new Vec3()): Vec3 {
    return this.orientation.axisX(out);
  }
}
